import { Fragment } from 'react';
import PropTypes from 'prop-types';
import Label from '../../../Label';
import formatBaggageForPassenger, {
  baggageAllowanceEquals,
  baggageVariesByTraveler,
  formatBaggageBaseLabel,
  getBaggageTooltipText,
  hasBaggage,
} from '../../../../helpers/formatBaggageForPassenger';
import formatStringToPascalCase from '../../../../helpers/formatters/formatStringToPascalCase';

/**
 * One baggage label of a single bag type, e.g. "1 checked bag" or "23kg carry-on".
 *
 * Tooltip (in priority order):
 * 1. Same allowance for all travelers — the carrier description ("UP TO 50
 *    POUNDS/23 KILOGRAMS..."), or "Weight allowance: 23kg per checked bag"
 *    when the carrier sent no description, or no tooltip when neither exists.
 * 2. This bag type differs between travelers (count, weight or description;
 *    requires `passengerInfos` + `bagKey`) — the "Varies by traveler"
 *    per-traveler breakdown instead.
 *
 * Shared by the flight result card, the More Classes list and the trip
 * configuration so wording/tooltip behavior stays identical everywhere.
 */
const BaggageAllowanceLabel = ({
  allowance,
  typeLabel,
  bagKey,
  passengerInfos,
  className,
  showInfoIndicator,
  tooltipOverride,
}) => {
  const varies =
    passengerInfos != null &&
    bagKey != null &&
    passengerInfos.length > 1 &&
    baggageVariesByTraveler(passengerInfos, bagKey);
  const baseText = formatBaggageBaseLabel(allowance, typeLabel);
  const tooltipText = getBaggageTooltipText(allowance, typeLabel);
  // `tooltipOverride` wins when provided (e.g. the per-flight breakdown), otherwise fall back
  // to the per-traveler breakdown or the carrier description.
  const tooltip =
    tooltipOverride ||
    (varies ? (
      <BaggageVariesTooltip infos={passengerInfos} bagKey={bagKey} typeLabel={typeLabel} />
    ) : (
      tooltipText && <span className="whitespace-pre-line">{tooltipText}</span>
    ));

  if (!tooltip) {
    return <span className={className}>{baseText}</span>;
  }

  return (
    <Label
      text={baseText}
      level=""
      inline
      className={className}
      descriptionOnHover={tooltip}
      tooltipPlacement="top"
      showInfoIndicator={showInfoIndicator}
    />
  );
};

BaggageAllowanceLabel.propTypes = {
  allowance: PropTypes.oneOfType([PropTypes.shape(), PropTypes.number]),
  typeLabel: PropTypes.string.isRequired,
  bagKey: PropTypes.oneOf(['carryOnBags', 'checkedBags']),
  passengerInfos: PropTypes.arrayOf(PropTypes.shape()),
  className: PropTypes.string,
  showInfoIndicator: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  tooltipOverride: PropTypes.node,
};

BaggageAllowanceLabel.defaultProps = {
  allowance: null,
  bagKey: null,
  passengerInfos: null,
  className: undefined,
  showInfoIndicator: false,
  tooltipOverride: null,
};

/**
 * Tooltip body listing the baggage allowance per passenger type.
 *
 * With `bagKey`/`typeLabel` (the normal case — tooltip of a "1 carry-on bag" /
 * "1 checked bag" label): lines cover only that bag type, each with the
 * carrier description (or weight/unit fallback) underneath:
 *   Adult: 1 carry-on bag
 *     UP TO 18 POUNDS/8 KILOGRAMS
 *   Lap Infant: No carry-on bags
 *
 * Without `bagKey` (only for the "No bags included" label — the primary
 * passenger has no bags, so no carry-on/checked labels exist; one tooltip
 * must explain what the OTHER travelers get, both bag types in one line):
 *   Adult: No bags included
 *   Child: 1 carry-on bag, 1 checked bag
 */
export const BaggageVariesTooltip = ({ infos, bagKey, typeLabel }) => (
  <div className="space-y-1">
    <div className="font-semibold">Varies by traveler</div>
    {infos.map((p) => {
      const pType = formatStringToPascalCase(p.passengerType || p.passengerTypeCode || '');
      const key = p.passengerTypeCode || p.passengerType;
      if (!bagKey) {
        return (
          <div key={key}>
            {`${pType}: ${formatBaggageForPassenger(p.carryOnBags, p.checkedBags) || 'No bags included'}`}
          </div>
        );
      }
      const allowance = p[bagKey];
      const baseText = formatBaggageBaseLabel(allowance, typeLabel) || `No ${typeLabel} bags`;
      const detailsText = getBaggageTooltipText(allowance, typeLabel);
      return (
        <div key={key}>
          <div>{`${pType}: ${baseText}`}</div>
          {detailsText ? (
            <div className="whitespace-pre-line pl-3 opacity-80">{detailsText}</div>
          ) : null}
        </div>
      );
    })}
  </div>
);

BaggageVariesTooltip.propTypes = {
  infos: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  bagKey: PropTypes.oneOf(['carryOnBags', 'checkedBags']),
  typeLabel: PropTypes.string,
};

BaggageVariesTooltip.defaultProps = {
  bagKey: null,
  typeLabel: '',
};

/**
 * Tooltip body listing the baggage allowance per flight within a single leg.
 * Used when the flights of one segment (e.g. a connection) carry different
 * allowances because their selected fares differ:
 *   MEX→AMS: 1 carry-on bag
 *   AMS→FRA: 1 carry-on bag, 1 checked bag
 * Each line shows that flight's primary (adult) passenger allowance.
 */
export const BaggageByFlightTooltip = ({ flights, bagKey, typeLabel }) => (
  <div className="space-y-1">
    <div className="font-semibold">Varies by flight</div>
    {flights.map((f, i) => {
      const prefix = f.routeLabel ? `${f.routeLabel}: ` : '';
      const key = f.routeLabel || i.toString();

      // Overview line (counts for both bag types) — only used when no bagKey is scoped.
      if (!bagKey) {
        return (
          <div key={key}>
            {`${prefix}${
              formatBaggageForPassenger(f.carryOnBags, f.checkedBags) || 'No bags included'
            }`}
          </div>
        );
      }

      // This flight's bag type ALSO differs between travelers — nest a per-traveler breakdown
      // under the flight so both dimensions are visible.
      const infos = f.passengerInfos || [];
      if (infos.length > 1 && baggageVariesByTraveler(infos, bagKey)) {
        return (
          <div key={key}>
            <div>{`${prefix}varies by traveler`}</div>
            {infos.map((p) => {
              const pType = formatStringToPascalCase(p.passengerType || p.passengerTypeCode || '');
              const pBase = formatBaggageBaseLabel(p[bagKey], typeLabel) || `No ${typeLabel} bags`;
              const pDetails = getBaggageTooltipText(p[bagKey], typeLabel);
              return (
                <div key={p.passengerTypeCode || p.passengerType} className="pl-3">
                  <div>{`${pType}: ${pBase}`}</div>
                  {pDetails ? (
                    <div className="whitespace-pre-line pl-3 opacity-80">{pDetails}</div>
                  ) : null}
                </div>
              );
            })}
          </div>
        );
      }

      // Same allowance for all travelers on this flight — single line + carrier description.
      const allowance = f[bagKey];
      const baseText = formatBaggageBaseLabel(allowance, typeLabel) || `No ${typeLabel} bags`;
      const detailsText = getBaggageTooltipText(allowance, typeLabel);
      return (
        <div key={key}>
          <div>{`${prefix}${baseText}`}</div>
          {detailsText ? (
            <div className="whitespace-pre-line pl-3 opacity-80">{detailsText}</div>
          ) : null}
        </div>
      );
    })}
  </div>
);

BaggageByFlightTooltip.propTypes = {
  flights: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  bagKey: PropTypes.oneOf(['carryOnBags', 'checkedBags']),
  typeLabel: PropTypes.string,
};

BaggageByFlightTooltip.defaultProps = {
  bagKey: null,
  typeLabel: '',
};

/**
 * Baggage for a leg whose flights carry different allowances. Shows the FIRST flight's
 * allowance as the visible label(s) — so it reads like any other leg — and reveals the full
 * per-flight breakdown (counts plus carrier description) in the hover tooltip, scoped to each
 * bag type. Optionally route-prefixed (e.g. "MEX→FRA:") when shown among multiple legs.
 */
export const BaggageByFlightLabel = ({ routeLabel, flights, className, showInfoIndicator }) => {
  const first = flights[0] || {};
  // A leg is "varies by flight" if EITHER bag type differs across its flights, but each bag
  // type must be judged on its own: only the type that actually differs gets the per-flight
  // tooltip. A type that is the same on every flight keeps its normal label/tooltip (carrier
  // description, or its own per-traveler breakdown).
  const typeVariesByFlight = (bagKey) =>
    flights.some((f) => !baggageAllowanceEquals(f[bagKey], first[bagKey]));
  const renderType = (bagKey, typeLabel) => {
    if (first[bagKey] == null) return null;
    const variesByFlight = typeVariesByFlight(bagKey);
    return (
      <BaggageAllowanceLabel
        allowance={first[bagKey]}
        typeLabel={typeLabel}
        bagKey={bagKey}
        passengerInfos={variesByFlight ? null : first.passengerInfos}
        className={className}
        showInfoIndicator={showInfoIndicator}
        tooltipOverride={
          variesByFlight ? (
            <BaggageByFlightTooltip flights={flights} bagKey={bagKey} typeLabel={typeLabel} />
          ) : null
        }
      />
    );
  };
  return (
    <>
      {routeLabel ? (
        <span className={className} style={{ fontWeight: 500 }}>
          {`${routeLabel}: `}
        </span>
      ) : null}
      {renderType('carryOnBags', 'carry-on')}
      {first.carryOnBags != null && first.checkedBags != null ? (
        <span className={className} style={{ fontSize: '0.8em' }}>
          {' + '}
        </span>
      ) : null}
      {renderType('checkedBags', 'checked')}
    </>
  );
};

BaggageByFlightLabel.propTypes = {
  routeLabel: PropTypes.string,
  flights: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  className: PropTypes.string,
  showInfoIndicator: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

BaggageByFlightLabel.defaultProps = {
  routeLabel: null,
  className: undefined,
  showInfoIndicator: false,
};

/**
 * Row of baggage labels for one or more legs:
 *   "MEX→FRA: 1 carry-on bag + 1 checked bag / FRA→MEX: 2 checked bags"
 * — carry-on and checked within a leg joined by "+", legs joined by " / "
 * (matching the combined brand/cabin line). When a leg supplies `routeLabel`
 * it is shown as a "MEX→FRA:" prefix so it is clear which allowance belongs to
 * which leg. A leg whose primary passenger has zero bags renders "No bags
 * included" instead of the two labels.
 *
 * Each entry of `allowances` is one leg: { carryOnBags, checkedBags,
 * passengerInfos, routeLabel } — the allowances of the leg's primary (adult)
 * passenger, the leg's full passenger list, and an optional origin→destination
 * route label. Every label decides its own tooltip via BaggageAllowanceLabel:
 * carrier description, or the "Varies by traveler" breakdown when its bag type
 * differs between that leg's travelers.
 */
export const BaggageAllowanceList = ({ allowances, showInfoIndicator, className }) => (
  <>
    {allowances.map((a, i) => {
      const none = !hasBaggage(a.carryOnBags) && !hasBaggage(a.checkedBags);
      const noneVaries =
        a.passengerInfos != null &&
        a.passengerInfos.length > 1 &&
        baggageVariesByTraveler(a.passengerInfos);
      return (
        <Fragment key={`leg-${i.toString()}`}>
          {i > 0 ? <span className={className}> / </span> : null}
          {a.routeLabel ? (
            <span className={className} style={{ fontWeight: 500 }}>
              {`${a.routeLabel}: `}
            </span>
          ) : null}
          {none ? (
            noneVaries ? (
              <Label
                text="No bags included"
                level=""
                inline
                className={className}
                descriptionOnHover={<BaggageVariesTooltip infos={a.passengerInfos} />}
                tooltipPlacement="top"
                showInfoIndicator={showInfoIndicator}
              />
            ) : (
              <span className={className}>No bags included</span>
            )
          ) : (
            <>
              {a.carryOnBags != null ? (
                <BaggageAllowanceLabel
                  allowance={a.carryOnBags}
                  typeLabel="carry-on"
                  bagKey="carryOnBags"
                  passengerInfos={a.passengerInfos}
                  className={className}
                  showInfoIndicator={showInfoIndicator}
                />
              ) : null}
              {a.carryOnBags != null && a.checkedBags != null ? (
                <span className={className} style={{ fontSize: '0.8em' }}>
                  {' + '}
                </span>
              ) : null}
              {a.checkedBags != null ? (
                <BaggageAllowanceLabel
                  allowance={a.checkedBags}
                  typeLabel="checked"
                  bagKey="checkedBags"
                  passengerInfos={a.passengerInfos}
                  className={className}
                  showInfoIndicator={showInfoIndicator}
                />
              ) : null}
            </>
          )}
        </Fragment>
      );
    })}
  </>
);

BaggageAllowanceList.propTypes = {
  allowances: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  showInfoIndicator: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  className: PropTypes.string,
};

BaggageAllowanceList.defaultProps = {
  showInfoIndicator: false,
  className: undefined,
};

export default BaggageAllowanceLabel;
